import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ScrollText,
  Code,
  Settings,
  Filter,
  User,
  ChevronRight,
  ExternalLink,
  Calendar,
  Sparkles,
  Bug,
  Trash2,
  Plus,
  Tag,
} from "lucide-react";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminGuard } from "@/lib/auth/AdminGuard";
import { formatDate } from "@/lib/utils";
import {
  getCodeChangelog,
  getConfigChangelog,
  type ConfigChangelogItem,
} from "@/lib/api/changelog";

export const Route = createFileRoute("/_dashboard/admin/changelog")({
  component: ChangelogPage,
});

// ---------------------------------------------------------------------------
// Action badge colours
// ---------------------------------------------------------------------------

function getActionBadge(action: string, label: string) {
  switch (action) {
    case "create":
      return <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/25 hover:bg-emerald-500/15">{label}</Badge>;
    case "update":
      return <Badge className="bg-blue-500/15 text-blue-600 border-blue-500/25 hover:bg-blue-500/15">{label}</Badge>;
    case "delete":
      return <Badge variant="destructive">{label}</Badge>;
    case "publish":
      return <Badge className="bg-violet-500/15 text-violet-600 border-violet-500/25 hover:bg-violet-500/15">{label}</Badge>;
    case "archive":
    case "unarchive":
      return <Badge variant="secondary">{label}</Badge>;
    case "duplicate":
      return <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/25 hover:bg-amber-500/15">{label}</Badge>;
    case "rollback":
      return <Badge className="bg-orange-500/15 text-orange-600 border-orange-500/25 hover:bg-orange-500/15">{label}</Badge>;
    default:
      return <Badge variant="outline">{label}</Badge>;
  }
}

// ---------------------------------------------------------------------------
// Changelog parsing
// ---------------------------------------------------------------------------

interface ChangelogItem {
  text: string;
  subItems: string[];
}

interface ChangelogCategory {
  name: string;
  items: ChangelogItem[];
}

interface ChangelogEntry {
  version: string;
  date: string;
  categories: ChangelogCategory[];
}

function parseChangelog(content: string): ChangelogEntry[] {
  const lines = content.split("\n");
  const entries: ChangelogEntry[] = [];
  let currentEntry: ChangelogEntry | null = null;
  let currentCategory: ChangelogCategory | null = null;

  for (const line of lines) {
    // Version header: ## [1.3.0] - 2026-02-13
    const versionMatch = line.match(/^## \[(.+?)\] - (.+)$/);
    if (versionMatch) {
      if (currentCategory && currentEntry) {
        currentEntry.categories.push(currentCategory);
      }
      if (currentEntry) {
        entries.push(currentEntry);
      }
      currentEntry = {
        version: versionMatch[1],
        date: versionMatch[2],
        categories: [],
      };
      currentCategory = null;
      continue;
    }

    // Category header: ### Corrections
    const categoryMatch = line.match(/^### (.+)$/);
    if (categoryMatch && currentEntry) {
      if (currentCategory) {
        currentEntry.categories.push(currentCategory);
      }
      currentCategory = { name: categoryMatch[1], items: [] };
      continue;
    }

    // List item: - Some text
    const itemMatch = line.match(/^- (.+)$/);
    if (itemMatch && currentCategory) {
      currentCategory.items.push({ text: itemMatch[1], subItems: [] });
      continue;
    }

    // Sub-list item:   - Sub text
    const subItemMatch = line.match(/^ {2}- (.+)$/);
    if (subItemMatch && currentCategory && currentCategory.items.length > 0) {
      currentCategory.items[currentCategory.items.length - 1].subItems.push(
        subItemMatch[1],
      );
      continue;
    }
  }

  // Push last category and entry
  if (currentCategory && currentEntry) {
    currentEntry.categories.push(currentCategory);
  }
  if (currentEntry) {
    entries.push(currentEntry);
  }

  return entries;
}

// ---------------------------------------------------------------------------
// Rendering helpers
// ---------------------------------------------------------------------------

function getCategoryStyle(name: string) {
  const lower = name.toLowerCase();
  if (lower.includes("amélioration") || lower.includes("enhancement")) {
    return {
      icon: Sparkles,
      color: "text-blue-600",
      bg: "bg-blue-500/10",
      badgeCls:
        "bg-blue-500/15 text-blue-700 border-blue-500/25 hover:bg-blue-500/15",
    };
  }
  if (lower.includes("correction") || lower.includes("fix")) {
    return {
      icon: Bug,
      color: "text-amber-600",
      bg: "bg-amber-500/10",
      badgeCls:
        "bg-amber-500/15 text-amber-700 border-amber-500/25 hover:bg-amber-500/15",
    };
  }
  if (lower.includes("suppression") || lower.includes("removed")) {
    return {
      icon: Trash2,
      color: "text-red-600",
      bg: "bg-red-500/10",
      badgeCls:
        "bg-red-500/15 text-red-700 border-red-500/25 hover:bg-red-500/15",
    };
  }
  if (lower.includes("ajout") || lower.includes("added")) {
    return {
      icon: Plus,
      color: "text-emerald-600",
      bg: "bg-emerald-500/10",
      badgeCls:
        "bg-emerald-500/15 text-emerald-700 border-emerald-500/25 hover:bg-emerald-500/15",
    };
  }
  return {
    icon: Tag,
    color: "text-gray-600",
    bg: "bg-gray-500/10",
    badgeCls:
      "bg-gray-500/15 text-gray-700 border-gray-500/25 hover:bg-gray-500/15",
  };
}

/** Render text with inline `code` and **bold** segments */
function renderInlineMarkdown(text: string) {
  const tokens = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/);
  return tokens.map((token, i) => {
    if (token.startsWith("`") && token.endsWith("`")) {
      return (
        <code
          key={i}
          className="px-1.5 py-0.5 rounded bg-muted font-mono text-xs text-foreground"
        >
          {token.slice(1, -1)}
        </code>
      );
    }
    if (token.startsWith("**") && token.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-foreground">
          {token.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{token}</span>;
  });
}

function formatChangelogDate(dateStr: string) {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Code Changelog tab
// ---------------------------------------------------------------------------

function CodeChangelogTab() {
  const { data, isLoading } = useQuery({
    queryKey: ["changelog-code"],
    queryFn: getCodeChangelog,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="overflow-hidden">
            <div className="px-6 py-4 bg-muted/30 border-b flex items-center gap-3">
              <Skeleton className="h-7 w-20 rounded-full" />
              <Skeleton className="h-4 w-32" />
            </div>
            <CardContent className="p-6 space-y-4">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-5/6" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!data?.content) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
            <ScrollText className="h-10 w-10 opacity-50" />
            <p>Aucun changelog disponible</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const entries = parseChangelog(data.content);

  return (
    <div className="space-y-6">
      {entries.map((entry, entryIndex) => (
        <Card
          key={entry.version}
          className="overflow-hidden"
          style={{
            animation: `fadeInUp 0.3s ease-out ${entryIndex * 0.05}s both`,
          }}
        >
          {/* Version header */}
          <div className="flex items-center gap-3 px-6 py-4 bg-muted/30 border-b">
            <Badge
              variant="outline"
              className="text-sm font-mono font-semibold px-3 py-1"
            >
              v{entry.version}
            </Badge>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              {formatChangelogDate(entry.date)}
            </div>
            {entryIndex === 0 && (
              <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/25 hover:bg-emerald-500/15 ml-auto">
                Dernière version
              </Badge>
            )}
          </div>

          {/* Categories */}
          <CardContent className="p-6 space-y-6">
            {entry.categories.map((category) => {
              const style = getCategoryStyle(category.name);
              const Icon = style.icon;
              const totalItems = category.items.reduce(
                (acc, item) => acc + 1 + item.subItems.length,
                0,
              );

              return (
                <div key={category.name}>
                  {/* Category header */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`p-1.5 rounded-md ${style.bg}`}>
                      <Icon className={`h-4 w-4 ${style.color}`} />
                    </div>
                    <h3 className="text-sm font-semibold">{category.name}</h3>
                    <Badge variant="secondary" className="text-xs h-5 px-1.5">
                      {totalItems}
                    </Badge>
                  </div>

                  {/* Items */}
                  <ul className="space-y-2 ml-9">
                    {category.items.map((item, i) => (
                      <li key={i}>
                        <div className="flex gap-2 text-sm leading-relaxed">
                          <ChevronRight className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground/50" />
                          <span className="text-muted-foreground">
                            {renderInlineMarkdown(item.text)}
                          </span>
                        </div>
                        {/* Sub-items */}
                        {item.subItems.length > 0 && (
                          <ul className="ml-6 mt-1.5 space-y-1">
                            {item.subItems.map((sub, j) => (
                              <li
                                key={j}
                                className="flex gap-2 text-sm leading-relaxed"
                              >
                                <span className="mt-0.5 shrink-0 text-muted-foreground/40">
                                  •
                                </span>
                                <span className="text-muted-foreground/80">
                                  {renderInlineMarkdown(sub)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Config Changelog tab
// ---------------------------------------------------------------------------

const ENTITY_TYPES = [
  { value: "simulator", label: "Simulateur" },
  { value: "simulator_version", label: "Version" },
  { value: "reference_table", label: "Table de référence" },
  { value: "form_field", label: "Champ de formulaire" },
  { value: "output_field", label: "Champ de sortie" },
  { value: "variable", label: "Variable" },
  { value: "variable_group", label: "Groupe de variables" },
  { value: "form_field_group", label: "Groupe de champs" },
  { value: "pricing_policy", label: "Politique tarifaire" },
  { value: "proposal_template", label: "Template de proposition" },
  { value: "test_scenario", label: "Scénario de test" },
];

const ACTIONS = [
  { value: "create", label: "Création" },
  { value: "update", label: "Modification" },
  { value: "delete", label: "Suppression" },
  { value: "publish", label: "Publication" },
  { value: "archive", label: "Archivage" },
  { value: "unarchive", label: "Désarchivage" },
  { value: "duplicate", label: "Duplication" },
  { value: "rollback", label: "Rollback" },
];

function ConfigChangelogTab() {
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Filters
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>("all");
  const [actionFilter, setActionFilter] = useState<string>("all");

  const { data, isLoading } = useQuery({
    queryKey: [
      "changelog-config",
      page,
      pageSize,
      entityTypeFilter,
      actionFilter,
    ],
    queryFn: () =>
      getConfigChangelog({
        page,
        page_size: pageSize,
        entity_type: entityTypeFilter !== "all" ? entityTypeFilter : undefined,
        action: actionFilter !== "all" ? actionFilter : undefined,
      }),
  });

  const resetFilters = () => {
    setEntityTypeFilter("all");
    setActionFilter("all");
    setPage(1);
  };

  const hasActiveFilters =
    entityTypeFilter !== "all" || actionFilter !== "all";

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium">Filtres</span>
            </div>

            {/* Entity type filter */}
            <Select
              value={entityTypeFilter}
              onValueChange={(v) => {
                setEntityTypeFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Type d'entité" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {ENTITY_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Action filter */}
            <Select
              value={actionFilter}
              onValueChange={(v) => {
                setActionFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les actions</SelectItem>
                {ACTIONS.map((a) => (
                  <SelectItem key={a.value} value={a.value}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                Réinitialiser
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Utilisateur</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Entité</TableHead>
                <TableHead>Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-4 w-28" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-28" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-6 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-28" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-40" />
                    </TableCell>
                  </TableRow>
                ))
              ) : data?.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-32 text-center">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <ScrollText className="h-10 w-10 opacity-50" />
                      <p>Aucune entrée trouvée</p>
                      {hasActiveFilters && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={resetFilters}
                        >
                          Réinitialiser les filtres
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                data?.items.map((item: ConfigChangelogItem, index: number) => (
                  <TableRow
                    key={item.id}
                    className="border-b transition-colors hover:bg-muted/50"
                    style={{
                      animation: `fadeInUp 0.3s ease-out ${index * 0.03}s both`,
                    }}
                  >
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {formatDate(item.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm truncate max-w-[150px]">
                          {item.user?.full_name || item.user?.email || "Système"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getActionBadge(item.action, item.action_label)}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {item.entity_type_label}
                      </span>
                    </TableCell>
                    <TableCell>
                      {item.entity_link ? (
                        <a
                          href={item.entity_link}
                          className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                        >
                          {item.entity_name}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-sm">{item.entity_name}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[300px] truncate">
                      {item.description || "-"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {data.total_items} entrée{data.total_items > 1 ? "s" : ""} au total
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Précédent
            </Button>
            <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded text-sm">
              {page} / {data.total_pages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setPage((p) => Math.min(data.total_pages, p + 1))
              }
              disabled={page === data.total_pages}
            >
              Suivant
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function ChangelogPage() {
  return (
    <AdminGuard>
      <div className="flex flex-col h-full">
        <Header title="Changelog" />

        <div className="flex-1 p-6 space-y-6 overflow-auto">
          <Tabs defaultValue="code">
            <TabsList>
              <TabsTrigger value="code" className="gap-2">
                <Code className="h-4 w-4" />
                Code
              </TabsTrigger>
              <TabsTrigger value="config" className="gap-2">
                <Settings className="h-4 w-4" />
                Configuration
              </TabsTrigger>
            </TabsList>

            <TabsContent value="code" className="mt-4">
              <CodeChangelogTab />
            </TabsContent>

            <TabsContent value="config" className="mt-4">
              <ConfigChangelogTab />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </AdminGuard>
  );
}
